import React, { useCallback, useEffect, useState } from 'react';
import ConfirmScreen from '../components/ConfirmScreen';
import { messageService } from '../service/messageService';
import { useTranslation } from 'react-i18next';
import useAxiosBuyer from '../hooks/useAxiosBuyer';
import { NewUserState } from '../store/newUserState';
import { Permissions } from '../store/permissions';
import { useAppDispatch, useAppSelector } from '../store';
import { OneIdentityService } from '../service/oneIdentityService';
import { StorageService } from '../helper/storage-service';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageBanner from './MessageBanner';
import { mapApiDataToStores } from '../helper/dataMapper';
import { userActions } from '../store/user';
import { authorizedOfficerActions } from '../store/authorizedOfficer';
import { companyActions } from '../store/company';
import { axiosBuyerPassthrough, axiosOneId } from '../hooks/axios';
import { permissionsActions } from '../store/permissions';
import DisabledUserWarning from '../components/DisabledUserWarning';

const { IconSuccess, dlsGreen } = require('@americanexpress/dls-react');
const {
    REGISTRY_ENDPOINT, AOF_URL, ERROR_URL, USER_DETAILS, UPDATE_PASSWORD_CREATION_STATUS_API_URL, USER_INFORMATION_URL
} = require('../helper/application-conf').default;
declare const window: any;


const SuccessNewUser = (props: { isDAO : any;}) => {
    const { t } = useTranslation();
    const newUser: NewUserState = useAppSelector((state) => state.newuser);
    const permissions: Permissions = useAppSelector((state) => state.permissions);
    const oneIdentityService = new OneIdentityService();
    const SITENAME = window.location.pathname.split('/')[2];
    const location = useLocation();
    const navigate = useNavigate();
    const profileCreationSuccess = location.state?.profileCreationSuccess;
    const [errorMessage, setErrorMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const genericError = t('genericError');
    const appDispatch = useAppDispatch();
    const storage = new StorageService();
    const { isDAO } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [isOneIdAuthenticated, setisOneIdAuthenticated] = useState(false);
    const [registryFailed, setRegistryFailed] = useState(false);
    const [registryApiFailed, setRegistryApiFailed] = useState(false);
    const [invalidPhone, setInvalidPhone] = useState(false);
    const [showContinueButton, setShowContinueButton] = useState(false);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = oneIdentityService.usmURL();
        script.async = true;
        script.onload = async () => {
            const isAuthenticated = await oneIdentityService.isOneIdAuthenticatedWithPolling();
            setisOneIdAuthenticated(isAuthenticated);
        };
        script.onerror = () => console.error('Failed to load script');
        document.body.appendChild(script);

        messageService.sendstepperToggle(false);
        window.scrollTo(0, 0)
        productRegistry();
    }, []);

    const login = () => {
        handleNavigation();
    };

    const redirectToOneIdentity = () => {
        window.location.href = `${oneIdentityService.oneIdentityURL()}?DestPage=${window.location.origin}/buyer-onboarding/${SITENAME}/created-amexid`;
    }

    const productRegistry = useCallback(async () => {
        setHasError(false)
        const payload = {
            "siteName": SITENAME,
            'isEligForAccountLinkEmail': false
        }
        if(!profileCreationSuccess) {
            payload['isEligForAccountLinkEmail'] = true;
        }
        await axiosOneId.post(REGISTRY_ENDPOINT, payload).then(async (response) => {
            if (response && (response.status == 201 || response.status == 200)) {
                await getUserDetails();
            }
            else if (response && response.status == 400) {
                setRegistryFailed(true);
            }
            else if (response && response.status == 401) {
                redirectToOneIdentity();
            }
            else {
                setRegistryApiFailed(true)
            }
        }, async (error) => {
            if (error && error.response && error.response.status === 400) {
                if (error.response.data.message === "Invalid cellphone from OneId") {
                    setInvalidPhone(true);
                }
                else {
                    setRegistryFailed(true);
                }
            }
            else if (error && error.response && error.response.status === 401) {
                redirectToOneIdentity();
            }
            else {
                setRegistryApiFailed(true)
            }
        });
    }, [])

    const handleError = (message: string) => {
        setHasError(true);
        setErrorMessage(message);
    };

    const getUserDetails = async () => {
        await axiosOneId.get(`${USER_DETAILS}?siteName=${SITENAME}`).then((response) => {
            if (response.status == 200) {
                if(response.data) {
                    setShowContinueButton(true);
                    appDispatch(permissionsActions.setPermissions(response.data));
                }
            }
            else {
                handleError(genericError);
            }
        }).catch((error) => {
            if (error && error.response && error.response.status === 400) {
                if (error.response.data.message === "Invalid cellphone from OneId") {
                    setInvalidPhone(true);
                }
                else {
                    handleError(genericError);
                }
            }
            else {
                handleError(genericError);
            }
        });
    }

    const handleNavigation = () => {
        if (permissions.isSystemAdministrator && isDAO) {
            const role = {
                userId: permissions.userId, emailId: permissions.emailId, firstName: permissions.firstName, lastName: permissions.lastName, roleNames: ['ADMINISTRATOR'], originalRole: JSON.parse(permissions.originalRole),
            };
            storage.setSession('role', JSON.stringify(role))
            setCreatedPasswordStatus(role)
            appDispatch(permissionsActions.setPermissions(permissions))
        }
        else {
            window.location.href = `${window.location.origin}/${SITENAME}/home`;
        }
    }

    const setCreatedPasswordStatus = useCallback(async (role: any) => {
        const headers = {
            'Content-Type': 'application/json',
            'x-user-context': role ? JSON.stringify(role) : ''
        }
        await axiosBuyerPassthrough.put(`${UPDATE_PASSWORD_CREATION_STATUS_API_URL}${SITENAME}`, null, {
            headers: headers
        }).then(() => {
            getSystemAdminInfo()
        }, () => {
            navigate(ERROR_URL);
        });
    }, []);


    const getSystemAdminInfo = async () => {
        setHasError(false)
        setIsLoading(true)
        axiosBuyerPassthrough.get(`${AOF_URL}${SITENAME}`
        ).then((response) => {
            const [userInfo, authorizedOfficerInfo, companyInfo] = mapApiDataToStores(response);
            const userStore: any = userInfo;
            const authorizedOfficerStore: any = authorizedOfficerInfo;
            const companyInfoStore: any = companyInfo;
            appDispatch(userActions.setUser(userStore));
            appDispatch(authorizedOfficerActions.setAuthorizedOfficer(authorizedOfficerStore));
            appDispatch(companyActions.setCompany(companyInfoStore));
            setIsLoading(false)
            navigate(USER_INFORMATION_URL);
        }, (error) => {
            if (error.response && error.response.status === 400 && error.response.data && error.response.data.errors[0].message === t('errors.submission_completed')) {
                setHasError(true)
                setIsLoading(false)
                setErrorMessage(error.response.data.errors[0].message);
            } else {
                setHasError(true)
                setIsLoading(false)
                setErrorMessage(genericError)
            }
        });
    };


    return (
        <div>
            <MessageBanner type="warning" messageText={errorMessage} showMessage={hasError} classes="col-md-offset-1 col-md-10" />
            {registryFailed && (
              <DisabledUserWarning t={t} warningType="disabled" />
            )}
            {registryApiFailed && (
              <DisabledUserWarning t={t} warningType="registryFailed" />
            )}
            {invalidPhone && (
              <DisabledUserWarning t={t} warningType="invalidPhone" />
            )}
            { (profileCreationSuccess && !registryFailed && !registryApiFailed && !invalidPhone) && 
            <ConfirmScreen icon={<IconSuccess
                id="icon-hover"
                size="lg"
                titleId="success"
                theme={dlsGreen}
                title="success"
            />} showContinueButton={showContinueButton} title={t('success_screen.title')} description={t('success_screen.description')} buttonText={t('success_screen.button_text_1')} handleClick={login}></ConfirmScreen>
            }
             { (isDAO && !profileCreationSuccess && !registryFailed && !registryApiFailed && !invalidPhone) && 
            <ConfirmScreen icon={<IconSuccess
                id="icon-hover"
                size="lg"
                titleId="success"
                theme={dlsGreen}
                title="success"
            />} showContinueButton={showContinueButton} title={t('success_screen.title_1')} description={t('success_screen.description_1')} buttonText={t('success_screen.button_text_1')} handleClick={login}></ConfirmScreen>
            }
        </div>
    );
}

export default SuccessNewUser;
